import { useIntl } from 'react-intl';

import { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { CreateEditOrganizationType } from '../components/OrganizationsListCreateEditDialog';

export const useOrganizationsListCreateEditDialogStrings = (
  dialogType: CreateEditOrganizationType,
  appErrorMessage?: string
) => {
  const intl = useIntl();

  // strings
  const dialogStrings = {
    title:
      dialogType === 'createOrganization'
        ? intl.formatMessage({
            id: 'organizations.createEditDialog.createTitle',
            defaultMessage: 'Create organization',
          })
        : intl.formatMessage({
            id: 'organizations.createEditDialog.editTitle',
            defaultMessage: 'Edit organization',
          }),
    description: intl.formatMessage({
      id: 'organizations.createEditDialog.createDescription',
      defaultMessage: 'All fields are required unless they’re marked optional.',
    }),

    // section titles
    organizationDetailsSection: intl.formatMessage({
      id: 'organizations.createEditDialog.section.organizationDetails',
      defaultMessage: 'Organization details',
    }),

    // organization name field
    organizationNameLabel: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNameLabel',
      defaultMessage: 'Organization name',
    }),
    organizationNamePlaceholder: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNamePlaceholder',
      defaultMessage: 'Enter organization name',
    }),
    organizationNameMaxLength: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNameMaxLength',
      defaultMessage: 'Organization name must be less than or equal to 50 characters',
    }),
    organizationNameVerantosError: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNameVerantosError',
      defaultMessage: 'Organization name cannot be Verantos',
    }),
    organizationNameInvalidCharactersError: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNameInvalidCharactersError',
      defaultMessage:
        'Name must contain letters, number, _ and -  and start with a letter or number',
    }),
    organizationNameStartEndCharactersError: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationNameStartEndCharactersError',
      defaultMessage:
        'Name must contain letters, number, _ and -  and start with a letter or number',
    }),

    // organization type field
    organizationTypeLabel: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationTypeLabel',
      defaultMessage: 'Organization type',
    }),
    organizationTypePlaceholder: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationTypePlaceholder',
      defaultMessage: 'Select organization type',
    }),

    // organization subtype field
    organizationSubtypeLabel: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationSubtypeLabel',
      defaultMessage: 'Organization sub type (Optional)',
    }),
    organizationSubtypePlaceholder: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationSubtypePlaceholder',
      defaultMessage: 'Select organization sub type',
    }),

    // organization key field
    organizationKeyLabel: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationKeyLabel',
      defaultMessage: 'Organization key',
    }),
    organizationKeyPlaceholder: intl.formatMessage({
      id: 'organizations.createEditDialog.organizationKeyPlaceholder',
      defaultMessage: 'Enter organization key',
    }),
  };

  // workflow dialog messages
  const apiWorkflowDialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText:
      dialogType === 'createOrganization'
        ? intl.formatMessage({
            id: 'organizations.createEditDialog.saveCreateBtnLabel',
            defaultMessage: 'Create organization',
          })
        : intl.formatMessage({
            id: 'organizations.createEditDialog.saveEditBtnLabel',
            defaultMessage: 'Save changes',
          }),
    successText:
      dialogType === 'createOrganization'
        ? intl.formatMessage({
            id: 'organizations.createEditDialog.successCreateTextCreate',
            defaultMessage: 'Organization was created successfully.',
          })
        : intl.formatMessage({
            id: 'organizations.createEditDialog.successEditTextCreate',
            defaultMessage: 'Organization was updated successfully.',
          }),
    submittingErrorText: appErrorMessage
      ? intl.formatMessage({
          id: 'organizations.createEditDialog.appErrorMessage',
          defaultMessage: appErrorMessage,
        })
      : intl.formatMessage({
          id: 'organizations.createEditDialog.errorTextEdit',
          defaultMessage: `Organization could not be ${
            dialogType === 'createOrganization' ? 'created' : 'updated'
          } due to a system error. You can retry ${
            dialogType === 'createOrganization' ? 'creating' : 'updating'
          } the organization by selecting the action below.`,
        }),
    submittingSaveButtonText:
      dialogType === 'createOrganization'
        ? intl.formatMessage({
            id: 'organizations.createEditDialog.submittingSaveBtnText',
            defaultMessage: 'Creating organization',
          })
        : intl.formatMessage({
            id: 'organizations.createEditDialog.savingChangesBtnLabel',
            defaultMessage: 'Saving changes',
          }),
    submittingErrorSaveButtonText: intl.formatMessage({
      id: 'organizations.createEditDialog.submittingErrorSaveBtnText',
      defaultMessage: 'Retry',
    }),
    submittingErrorCancelButtonText: intl.formatMessage({
      id: 'organizations.createEditDialog.submittingErrorCancelBtnText',
      defaultMessage: 'Close',
    }),
  };

  return { dialogStrings, apiWorkflowDialogMessages };
};
